import React, { useState, memo, useMemo } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import CloseIcon from "@mui/icons-material/Close";

import { api } from "../services/api"; // <-- your api service
import DynamicListeners from "../pages/alb/DynamicListeners";
import DynamicRules from "../pages/sgrule/DynamicRules";
import RoleGuard from "./RoleGuard";

const RowActionMenu = memo(function RowActionMenu({
  row,
  actionIconMap,
  onAction,           // still used for other mapped actions
  resolveStatus,      // required to compute showStartStop
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  // Dialog (View Details)
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [loadingView, setLoadingView] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [resourceDetails, setResourceDetails] = useState([]);
  const [viewResponse, setViewResponse] = useState({});

  // Backend enabled actions from row
  const enabledActions = (row.actionCode || []).filter(a => a.IsEnabled);

  // Start/Stop visibility logic (kept same as you wrote)
  const resourceStatus = resolveStatus(row);
  const showStartStop =
    !row.indent &&
    !["security groups", "s3 bucket", "alb", "nlb", "route 53", "rds"].includes((row.resourceName || "").toLowerCase()) &&
    row.createdBy !== 0 &&
    (resourceStatus === "running" || resourceStatus === "stopped") &&
    row.isResourceCreatedByReqUser &&
    row.isResourceAddedFromCMP;

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };

  // ---- View Details (self-contained) ----
  const handleViewClick = async (ticket) => {
    try {
      // Open dialog immediately and show loading
      setOpenViewDialog(true);
      setLoadingView(true);
      setSelectedTicket(ticket);

      const response = await api.get(
        `/api/Resources/viewResourceRequest/${ticket.resourceId}`,
        {
          headers: {
            "X-CORRELATION-ID": "0d89a8fe-6a3f-46e6-8bde-72daf869b07c",
          },
        }
      );

      setResourceDetails(response.data?.resourceRequestDetails || []);
      setViewResponse(response.data || {});
    } catch (error) {
      console.error("Error fetching resource details:", error);
    } finally {
      setLoadingView(false);
    }
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setSelectedTicket(null);
    setResourceDetails([]);
    setViewResponse({});
  };

  // Group details by tabName (same as your code)
  const groupedDetails = useMemo(() => {
    if (!Array.isArray(resourceDetails)) return {};
    return resourceDetails.reduce((acc, detail) => {
      const tab = detail.tabName || "General";
      if (!acc[tab]) acc[tab] = [];
      acc[tab].push(detail);
      return acc;
    }, {});
  }, [resourceDetails]);

  // Render order of tabs
  const orderedTabs = useMemo(() => {
    const base = ["General", "Disks", "Networking"];
    const others = Object.keys(groupedDetails || {})
      .filter((t) => !["General", "Tag", "Disks", "Networking"].includes(t));
    return [...base, ...others].filter(
      (tab) => groupedDetails[tab] && groupedDetails[tab].length > 0
    );
  }, [groupedDetails]);

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      {/* Trigger Button */}
      <Tooltip title="More Actions">
        <IconButton
          size="small"
          sx={{ color: "primary.main" }}
          onClick={handleOpen}
        >
          <MoreVertOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {/* View Details (opens internal dialog) */}
        <MenuItem
          dense
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
            handleViewClick(row); // <-- open dialog here
          }}
        >
          <ListItemIcon sx={{ minWidth: 30 }}>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="View Details"
            primaryTypographyProps={{ variant: "body2" }}
          />
        </MenuItem>

        {/* Start / Stop Instance */}
        {showStartStop && (
          <RoleGuard requiredPermission="START_STOP">
            <MenuItem
              dense
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
                // Delegate to parent handler for your existing dialog/workflow
                onAction?.("START_STOP_MENU", row);
              }}
            >
              <ListItemIcon sx={{ minWidth: 30 }}>
                <PowerSettingsNewOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Start / Stop Instance"
                primaryTypographyProps={{ variant: "body2" }}
              />
            </MenuItem>
          </RoleGuard>
        )}

        {/* Backend mapped actions */}
        {enabledActions.length
          ? enabledActions.map((a) => (
            <MenuItem
              key={a.ActionCode}
              dense
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
                onAction?.(a.ActionCode, row);
              }}
            >
              <ListItemIcon sx={{ minWidth: 30 }}>
                {actionIconMap?.[a.ActionCode] || (
                  <FileCopyOutlinedIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={a.ActionName || a.ActionCode}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </MenuItem>
          ))
          : null}
      </Menu>

      {/* --- View Details Dialog (self-contained) --- */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: "bold",
            bgcolor: theme.palette.background.default,
            color: theme.palette.text.primary,
            fontSize: theme.typography.h5.fontSize,
          }}
        >
          View Details
          <IconButton aria-label="close" onClick={handleCloseViewDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            bgcolor: theme.palette.background.default,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            borderBottom: "1px solid",
            borderColor: "divider"
          }}
        >
          {/* Loading */}
          {loadingView ? (
            <Box
              sx={{
                width: "100%",
                py: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography>Fetching resource details…</Typography>
            </Box>
          ) : (
            <>
              {/* Sections: General / Disks / Networking / Others */}
              {orderedTabs.map((tabName) => (
                <Box key={tabName} sx={{ mb: 2, width: "100%" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1,
                      fontWeight: "bold",
                      color: theme.palette.text.primary,
                      fontSize: theme.typography.h6.fontSize,
                      position: "relative",
                      display: "inline-block",
                      "&:after": {
                        content: '""',
                        display: "block",
                        width: "50%",
                        borderBottom: `1px solid ${theme.palette.primary.main}`,
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                      },
                    }}
                  >
                    {tabName}
                  </Typography>

                  {(groupedDetails[tabName] || []).map((detail) => {
                    const isListeners = detail.fieldName === "Listeners";
                    const isRules = detail.fieldName === "InboundRules" || detail.fieldName === "OutboundRules";
                    const isIvantiConfig = detail.fieldName === "ivantiConfiguration";

                    if (isIvantiConfig) {
                      let parsed = null;
                      try {
                        parsed = typeof detail.fieldData === "string" ? JSON.parse(detail.fieldData) : detail.fieldData;
                      } catch (e) {
                        console.error("Failed to parse ivantiConfiguration", e);
                      }

                      if (parsed && Object.keys(parsed).length > 0) {
                        return (
                          <Box key={detail.requestId || detail.fieldName} sx={{ width: "100%", mt: 2, mb: 1 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: "bold", mb: 1, color: "text.primary" }}
                            >
                              Ivanti Configuration
                            </Typography>

                            {Object.entries(parsed).map(([key, val]) => {
                              let displayValue = "N/A";

                              if (Array.isArray(val)) {
                                displayValue = val.map((v) => v?.text || v?.value || v).join(", ");
                              } else if (val && typeof val === "object") {
                                displayValue = val.text || val.value || "N/A";
                              } else if (val !== null && val !== undefined) {
                                displayValue = String(val);
                              }

                              const formattedLabel = key.replace(/([A-Z])/g, " $1").trim();

                              return (
                                <Box
                                  key={key}
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "220px 1fr", // FIXED WIDTH KEEPS VALUES CLOSE TO LABELS
                                    alignItems: "center",
                                    py: 1,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      textTransform: "capitalize",
                                      color: "text.secondary",
                                    }}
                                  >
                                    {formattedLabel}:
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isDark ? "#90caf9" : "#1976d2",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    {displayValue}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      }
                      return null;
                    }

                    if (isListeners || isRules) {
                      return (
                        <Box key={detail.requestId || detail.fieldName} sx={{ py: 1, width: "100%" }}>
                          {isListeners ? (
                            <DynamicListeners fieldData={detail.fieldData} tablefrom={true} />
                          ) : (
                            <DynamicRules fieldData={detail.fieldData} title={detail.fieldName} />
                          )}
                        </Box>
                      );
                    }

                    return (
                      <Box
                        key={detail.requestId || detail.fieldName}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "220px 1fr", // ALIGNS WITH IVANTI BLOCK, REMOVES MIDDLE SPACE
                          alignItems: "center",
                          py: 1,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            textTransform: "capitalize",
                            color: "text.secondary",
                          }}
                        >
                          {detail.fieldName}:
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: isDark ? "#90caf9" : "#1976d2",
                            wordBreak: "break-word"
                          }}
                        >
                          {detail.fieldData || "N/A"}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ))}
              {/* Price Details */}
              <Box sx={{ mb: 2, width: "100%" }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    fontWeight: "bold",
                    color: theme.palette.text.primary,
                    fontSize: theme.typography.h6.fontSize,
                    position: "relative",
                    display: "inline-block",
                    "&:after": {
                      content: '""',
                      display: "block",
                      width: "50%",
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                    },
                  }}
                >
                  Price Details
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        mr: 2,
                        mb: 1,
                        textTransform: "capitalize",
                        color: theme.palette.text.secondary,
                        fontSize: theme.typography.body2.fontSize,
                      }}
                    >
                      Total Forecasted Price:
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: theme.typography.body2.fontSize,
                        fontWeight: "bold",
                      }}
                    >
                      {viewResponse?.totalPrice
                        ? `$${viewResponse.totalPrice}`
                        : "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Tags */}
              <Box
                sx={{
                  mb: 2,
                  mt: 2,
                  display: "flex",
                  justifyContent: "flex-start",
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                {(resourceDetails || [])
                  .filter((d) => d.tabName === "Tag")
                  .map((detail) => (
                    <Box key={`tag-${detail.requestId}`}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "capitalize",
                          fontSize: theme.typography.body2.fontSize,
                          bgcolor: isDark ? 'rgba(144, 202, 249, 0.12)' : 'rgba(223, 237, 255, 0.6)',
                          color: isDark ? '#90caf9' : theme.palette.primary.main,
                          m: 1,
                          px: 2,
                          paddingTop: "5px",
                          paddingBottom: "5px",
                          borderRadius: 2,
                          display: "inline-block",
                          textAlign: "center",
                        }}
                      >
                        {detail.fieldData}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default RowActionMenu;
